import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, CheckCircle, User, Phone, Mail, Home, Calendar, Heart, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useAutoSave from '@/hooks/useAutoSave';
import AutoSaveIndicator from '@/components/emr/AutoSaveIndicator';
import EMRConsentForm from '@/components/emr/EMRConsentForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewPatientForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentData, setConsentData] = useState(null);

  const initialFormData = {
    first_name: '',
    last_name: '',
    dob: '',
    allergies: '',
    anticoagulant: false,
    cell_number: '',
    email: '',
    home_address: '',
    pmh_neuro: '',
    pmh_cardio: '',
    pmh_pulmonary: '',
    pmh_renal: '',
    pmh_gastro: '',
    current_medications: '',
    // Vital Signs
    blood_pressure: '',
    temperature: '',
    oxygen_saturation: '',
    heart_rate: '',
    respiratory_rate: '',
    iv_access_location: ''
  };

  const {
    formData,
    updateField,
    isSaving,
    lastSaved,
    hasDraft,
    loadDraft,
    clearDraft,
    resetForm
  } = useAutoSave('new_patient', initialFormData);

  // Show draft prompt on mount if draft exists
  React.useEffect(() => {
    if (hasDraft && !formData.first_name) {
      setShowDraftPrompt(true);
    }
  }, [hasDraft]);

  const handleLoadDraft = () => {
    loadDraft();
    setShowDraftPrompt(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftPrompt(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Show consent form before submission
    setShowConsent(true);
  };

  const handleConsentSigned = async (consent) => {
    setConsentData(consent);
    setShowConsent(false);
    setLoading(true);
    setError('');

    try {
      const patientDataWithConsent = {
        ...formData,
        consent_signed: true,
        consent_signature: consent.signature,
        consent_timestamp: consent.timestamp
      };

      const response = await axios.post(
        `${API}/emr/patients`,
        patientDataWithConsent,
        { withCredentials: true }
      );

      if (response.data) {
        setSuccess(true);
        clearDraft(); // Clear auto-saved draft after successful submission
        setTimeout(() => {
          navigate(`/emr/patient/${response.data.id}`);
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create patient record');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentCancel = () => {
    setShowConsent(false);
  };

  if (!user) {
    navigate('/');
    return null;
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-green-400 mb-2">Patient Created Successfully!</h2>
          <p className="text-gray-400">Redirecting to patient chart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/emr')}
                className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
            <AutoSaveIndicator isSaving={isSaving} lastSaved={lastSaved} />
          </div>
        </div>
      </div>

      {/* Draft Recovery Prompt */}
      {showDraftPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-yellow-500/30 rounded-2xl p-8 max-w-md">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-yellow-400 mb-2 text-center">Unsaved Draft Found</h3>
            <p className="text-gray-300 mb-6 text-center">
              We found an unsaved patient intake form. Would you like to continue where you left off?
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleLoadDraft}
                className="flex-1 bg-yellow-400 text-black font-bold py-3 rounded-lg hover:bg-yellow-500 transition-colors"
              >
                Load Draft
              </button>
              <button
                onClick={handleDiscardDraft}
                className="flex-1 border border-gray-600 text-gray-300 font-bold py-3 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-yellow-400 mb-2">EliteIV EMR - New Patient Intake</h1>
            <p className="text-gray-400">All fields are auto-saved as you type. Don't worry about losing your work!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <User className="w-6 h-6 mr-2" />
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Enter last name"
                  />
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <Phone className="w-6 h-6 mr-2" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dob}
                    onChange={(e) => updateField('dob', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Cell Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.cell_number}
                    onChange={(e) => updateField('cell_number', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Home className="w-4 h-4 inline mr-2" />
                    Home Address
                  </label>
                  <input
                    type="text"
                    value={formData.home_address}
                    onChange={(e) => updateField('home_address', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="123 Main St, City, State ZIP"
                  />
                </div>
              </div>
            </section>

            {/* Medical Information */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <Heart className="w-6 h-6 mr-2" />
                Medical Information
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Allergies</label>
                  <textarea
                    value={formData.allergies}
                    onChange={(e) => updateField('allergies', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="List any known allergies (medications, food, environmental)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Current Medications</label>
                  <textarea
                    value={formData.current_medications}
                    onChange={(e) => updateField('current_medications', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="List all current medications and supplements"
                  />
                </div>
                <div className="flex items-center space-x-3 bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <input
                    type="checkbox"
                    id="anticoagulant"
                    checked={formData.anticoagulant}
                    onChange={(e) => updateField('anticoagulant', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                  />
                  <label htmlFor="anticoagulant" className="text-white font-medium cursor-pointer">
                    Patient is taking anticoagulants (blood thinners)
                  </label>
                </div>
              </div>
            </section>

            {/* Past Medical History */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <FileText className="w-6 h-6 mr-2" />
                Past Medical History - Physical Assessment
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Neurological</label>
                  <textarea
                    value={formData.pmh_neuro}
                    onChange={(e) => updateField('pmh_neuro', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any neurological conditions or history"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Cardiovascular</label>
                  <textarea
                    value={formData.pmh_cardio}
                    onChange={(e) => updateField('pmh_cardio', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any heart or cardiovascular conditions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pulmonary</label>
                  <textarea
                    value={formData.pmh_pulmonary}
                    onChange={(e) => updateField('pmh_pulmonary', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any lung or respiratory conditions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Renal</label>
                  <textarea
                    value={formData.pmh_renal}
                    onChange={(e) => updateField('pmh_renal', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any kidney or urinary conditions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Gastrointestinal</label>
                  <textarea
                    value={formData.pmh_gastro}
                    onChange={(e) => updateField('pmh_gastro', e.target.value)}
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Any digestive or GI conditions"
                  />
                </div>
              </div>
            </section>

            {/* Vital Signs */}
            <section>
              <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                <Heart className="w-6 h-6 mr-2" />
                Vital Signs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Blood Pressure
                  </label>
                  <input
                    type="text"
                    value={formData.blood_pressure}
                    onChange={(e) => updateField('blood_pressure', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 120/80"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Temperature
                  </label>
                  <input
                    type="text"
                    value={formData.temperature}
                    onChange={(e) => updateField('temperature', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 98.6°F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Oxygen Saturation (O2)
                  </label>
                  <input
                    type="text"
                    value={formData.oxygen_saturation}
                    onChange={(e) => updateField('oxygen_saturation', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 98%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Heart Rate
                  </label>
                  <input
                    type="text"
                    value={formData.heart_rate}
                    onChange={(e) => updateField('heart_rate', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 72 bpm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Respiratory Rate
                  </label>
                  <input
                    type="text"
                    value={formData.respiratory_rate}
                    onChange={(e) => updateField('respiratory_rate', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., 16/min"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    IV Access Location
                  </label>
                  <input
                    type="text"
                    value={formData.iv_access_location}
                    onChange={(e) => updateField('iv_access_location', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., Left antecubital, Right hand"
                  />
                </div>
              </div>
            </section>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">Error Creating Patient</p>
                  <p className="text-red-300 text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure? Unsaved changes will be kept as a draft.')) {
                    navigate('/emr');
                  }
                }}
                className="px-6 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all fields?')) {
                      resetForm();
                    }
                  }}
                  className="px-6 py-3 border border-red-600 text-red-400 font-medium rounded-lg hover:bg-red-600/10 transition-colors"
                >
                  Clear Form
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save className="w-5 h-5" />
                  <span>{loading ? 'Creating Patient...' : 'Create Patient Record'}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Consent Form Modal */}
      {showConsent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="max-w-4xl w-full">
            <EMRConsentForm
              patientName={`${formData.first_name} ${formData.last_name}`.trim()}
              onConsentSigned={handleConsentSigned}
              onCancel={handleConsentCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPatientForm;
